/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useTranslation } from 'react-i18next';

// UI lib dependencies
import { Link, LinkProps, useParams, useLocation } from 'react-router-dom';
import { Flex, NavLink, Tooltip } from '@mantine/core';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// Auth
import useAccount from '@features/Account/store/useAccount';
import { isSpecialUserEmail } from '@shared/constants/users';

// Assets
import {
  IconFolders as IconMy,
  IconStar as IconFavorites,
  IconClockHour4 as IconRecent,
  IconEyeOff as IconRedact,
  IconLock as IconPermissions,
  IconUsers,
  IconBrandGoogleAnalytics as IconAnalytics,
  IconScale as IconDueDiligence,
  IconChecklist as IconRequestList,
  IconTrash as IconArchived,
  IconMessageQuestion as IconSupport,
  IconSettings,
  IconProps,
  IconFileTextAi as IconAIAssistant,
} from '@tabler/icons-react';

// Types
import { ResourceScope } from '@app-types/blackroom-app';

// Style
import { ICON_SIZE_MAP } from '@ui-kit/icons-sizes';
import * as classes from './styles/index.css';
import { NavBarLevel } from '../types';
import BetaBadge from '@app/shared/ui-kit/molecules/BetaBadge';
import { useNavBar } from '../../store/navBarState';
import { ForwardRefExoticComponent, RefAttributes } from 'react';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

interface ItemsProps {
  level: NavBarLevel;
}

type MenuItemDescriptor = {
  icon: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
  path: LinkProps;
  level: NavBarLevel;
  access?: 'workspace-admin' | 'workspace-editor' | 'tenant-admin' | 'user';
  shallow?: boolean;
  beta?: boolean;
};

const MenuItemDescriptors: Record<string, MenuItemDescriptor> = {};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * Renders menu items
 * @param level Level of the menu item groups
 * (primary or secondary, i.e. top or bottom)
 */
function LevelItems({ level = NavBarLevel.Primary, ...props }: ItemsProps) {
  /* ********************************** HOOKS ********************************* */

  // Context
  const workspace = useWorkspace();
  const navBar = useNavBar();

  // Auth
  const { isWorkspaceAdmin, hasPermission, account } = useAccount();

  // Routing
  const { scope } = useParams<{ scope: ResourceScope }>();
  const { pathname } = useLocation();

  MenuItemDescriptors.my = {
    access: 'user',
    icon: IconMy,
    level: NavBarLevel.Primary,
    path: { to: `/workspace/${workspace.id}/resources/my` },
  };

  MenuItemDescriptors.favorites = {
    access: 'user',
    icon: IconFavorites,
    level: NavBarLevel.Primary,
    path: { to: `/workspace/${workspace.id}/resources/favorites` },
  };

  MenuItemDescriptors.recent = {
    access: 'user',
    icon: IconRecent,
    level: NavBarLevel.Primary,
    path: { to: `/workspace/${workspace.id}/resources/recent` },
  };

  MenuItemDescriptors.archived = {
    access: 'workspace-editor',
    level: NavBarLevel.Secondary,
    icon: IconArchived,
    path: { to: `/workspace/${workspace.id}/resources/archived` },
  };

  MenuItemDescriptors.users = {
    access: 'workspace-admin',
    level: NavBarLevel.Primary,
    icon: IconUsers,
    path: { to: `/workspace/${workspace.id}/users` },
    shallow: false,
  };

  MenuItemDescriptors.permissions = {
    access: 'workspace-admin',
    level: NavBarLevel.Primary,
    icon: IconPermissions,
    path: { to: `/workspace/${workspace.id}/permissions` },
    shallow: false,
  };

  MenuItemDescriptors.redact = {
    access: 'workspace-admin',
    level: NavBarLevel.Primary,
    icon: IconRedact,
    path: { to: `/workspace/${workspace.id}/redactions` },
    beta: false,
  };

  MenuItemDescriptors.support = {
    access: 'user',
    level: NavBarLevel.Primary,
    icon: IconSupport,
    path: { to: `/workspace/${workspace.id}/support` },
    shallow: false,
  };

  MenuItemDescriptors.analytics = {
    access: 'workspace-admin',
    level: NavBarLevel.Primary,
    icon: IconAnalytics,
    path: { to: `/workspace/${workspace.id}/analytics` },
    shallow: false,
  };

  MenuItemDescriptors.AIAssistant = {
    access: 'user',
    level: NavBarLevel.Primary,
    icon: IconAIAssistant,
    path: { to: `/workspace/${workspace.id}/AIAssistant` },
    shallow: false,
    beta: false,
  };

  MenuItemDescriptors.dueDiligence = {
    access: 'workspace-admin',
    level: NavBarLevel.Primary,
    icon: IconDueDiligence,
    path: { to: `/workspace/${workspace.id}/due-diligence` },
    shallow: false,
  };

  MenuItemDescriptors.requestList = {
    access: 'workspace-editor',
    level: NavBarLevel.Primary,
    icon: IconRequestList,
    path: { to: `/workspace/${workspace.id}/request-list` },
    shallow: false,
  };

  MenuItemDescriptors.settings = {
    access: 'workspace-admin',
    level: NavBarLevel.Secondary,
    icon: IconSettings,
    path: { to: `/workspace/${workspace.id}/settings` },
    shallow: false,
  };

  // Localization
  const { t } = useTranslation(['Common', 'WorkspaceLayout']);

  /* ***************************** LOCAL VARIABLES **************************** */

  /**
   * Builds menu items based on translation file
   * @returns {Array} Array containing menu item descriptors
   */
  const leveledMenuItems = (): (MenuItemDescriptor & {
    pathKey: string;
    label: string;
  })[] =>
    Object.entries(MenuItemDescriptors)
      .filter(([pathKey, { level: itemLevel, access }]) => {
        let granted: boolean = false;
        if (
          pathKey === 'AIAssistant' &&
          import.meta.env.VITE_APP_AIASSISTANT !== 'true'
        ) {
          return false;
        }
        if (
          pathKey === 'redact' &&
          import.meta.env.VITE_APP_REDACT !== 'true' &&
          // @TODO: Temp., remove me
          !isSpecialUserEmail(account?.email)
        ) {
          return false;
        }
        if (
          pathKey === 'support' &&
          import.meta.env.VITE_APP_SUPPORT !== 'true'
        ) {
          return false;
        }
        if (pathKey === 'dueDiligence' && !isSpecialUserEmail(account?.email)) {
          return false;
        }
        if (
          pathKey === 'requestList' &&
          !workspace.workspaceSettings.modules.requestList
        ) {
          return false;
        }
        if (
          !access ||
          (isWorkspaceAdmin(workspace) && access === 'workspace-admin') ||
          (access === 'workspace-editor' &&
            (isWorkspaceAdmin(workspace) ||
              hasPermission('manageResources'))) ||
          access === 'user'
        ) {
          granted = true;
        }
        return itemLevel === level && granted;
      })
      .map(([pathKey, descriptor]) => ({
        pathKey,
        label: t(`WorkspaceLayout:Menu.${level}Items.${pathKey}.label`),
        ...descriptor,
      }));

  /* ******************************** CALLBACKS ******************************* */
  /**
   * Closes resource menu when changing navigation menu  section
   */
  function closeResourceMenu() {}
  /* ******************************** RENDERING ******************************* */

  return (
    <Tooltip.Group openDelay={500} closeDelay={100}>
      <Flex gap={3} justify="center" direction="column" wrap="wrap">
        {leveledMenuItems().map(
          ({ label, icon: Icon, pathKey, path, beta }) => {
            const isActive =
              scope === `${pathKey}` || pathname.includes(pathKey);
            return (
              <Tooltip
                label={label}
                position="right"
                offset={24}
                key={pathKey}
                zIndex={10010000000}
                style={{ position: 'fixed' }}
                className={classes.tooltip}
                arrowSize={6}
              >
                <NavLink
                  w="100%"
                  to={path.to}
                  className={isActive ? classes.itemActive : classes.item}
                  classNames={{
                    body: classes.body,
                    label: isActive ? classes.labelActive : classes.label,
                    root: classes.root,
                    section: classes.section,
                  }}
                  variant="filled"
                  component={Link}
                  label={label}
                  leftSection={
                    <Icon
                      className={isActive ? classes.iconActive : classes.icon}
                      size={ICON_SIZE_MAP.medium}
                    />
                  }
                  rightSection={beta && navBar.isExpanded && <BetaBadge />}
                  onClick={async function cb() {
                    if (pathKey !== scope) {
                      closeResourceMenu();
                    }
                  }}
                  replace
                />
              </Tooltip>
            );
          },
        )}
      </Flex>
    </Tooltip.Group>
  );
}

export default LevelItems;
